import React, { useState } from 'react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Send, Mail, Check, AlertCircle, CheckCircle } from 'lucide-react';

const NEXT_STEPS = [
  "You'll hear back within 48 hours.",
  "Custom pricing is discussed before any commitment.",
  "If you're not fully sure what you need yet, that's completely fine.",
];

function getResultStyle(type) {
  if (type === 'success') return { border: 'border-[#14B8A6]/30 bg-[#14B8A6]/5', text: 'text-[#14B8A6]' };
  if (type === 'warning') return { border: 'border-yellow-500/30 bg-yellow-500/5', text: 'text-yellow-400' };
  return { border: 'border-red-500/30 bg-red-500/5', text: 'text-red-400' };
}

function getResultIcon(type) {
  if (type === 'success') return <CheckCircle className="w-5 h-5 text-[#14B8A6] flex-shrink-0 mt-0.5" />;
  if (type === 'warning') return <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />;
  return <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />;
}

const FormResult = ({ result }) => {
  if (!result) return null;
  const style = getResultStyle(result.type);
  return (
    <div className={`flex items-start gap-3 p-4 rounded-sm border ${style.border}`} data-testid="contact-form-result">
      {getResultIcon(result.type)}
      <p className={`text-sm ${style.text}`}>{result.message}</p>
    </div>
  );
};

async function submitForm(formData) {
  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      name: formData.name,
      email: formData.email,
      subject: '',
      message: formData.message,
      source_page: 'contact',
    }),
  });
  const data = await response.json();
  if (!response.ok) {
    return { type: 'error', message: data.detail || 'Something went wrong. Please try again.' };
  }
  if (data.status === 'success') {
    return { type: 'success', message: 'Your message has been received and a notification has been sent.' };
  }
  return { type: 'warning', message: 'Your message was received, but email notification had an issue.' };
}

const ContactForm = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const res = await submitForm(formData);
      setResult(res);
      if (res.type !== 'error') setFormData({ name: '', email: '', message: '' });
    } catch (err) {
      setResult({ type: 'error', message: 'Network error. Please check your connection and try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
      <FormResult result={result} />
      <div>
        <Label htmlFor="name" className="label-sm text-[#E8E4DF]/70 mb-2 block" data-font="label">Name *</Label>
        <Input id="name" name="name" value={formData.name} onChange={handleChange} required className="bg-transparent border-[#3d2a5c]/30 text-[#F0EDE8] focus:border-[#14B8A6] h-11 rounded-none border-0 border-b text-lg" data-testid="contact-name-input" />
      </div>
      <div>
        <Label htmlFor="email" className="label-sm text-[#E8E4DF]/70 mb-2 block" data-font="label">Email *</Label>
        <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required className="bg-transparent border-[#3d2a5c]/30 text-[#F0EDE8] focus:border-[#14B8A6] h-11 rounded-none border-0 border-b text-lg" data-testid="contact-email-input" />
      </div>
      <div>
        <Label htmlFor="message" className="label-sm text-[#E8E4DF]/70 mb-2 block" data-font="label">Message *</Label>
        <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required placeholder="Tell me about your world — your characters, the atmosphere, the feeling you want to capture" rows={5} className="bg-transparent border-[#3d2a5c]/30 text-[#F0EDE8] focus:border-[#14B8A6] rounded-none border-0 border-b resize-none placeholder:text-[#E8E4DF]/30 text-lg" data-testid="contact-message-input" />
      </div>
      <div className="pt-4" data-font="label">
        <button type="submit" disabled={submitting} className="w-full bg-[#14B8A6] hover:bg-[#0D9488] disabled:opacity-50 disabled:cursor-not-allowed text-[#0A0A0B] font-medium py-3.5 text-sm uppercase tracking-[0.12em] transition-all duration-300 hover:shadow-lg hover:shadow-[#14B8A6]/20 flex items-center justify-center gap-2" data-testid="contact-submit-btn">
          <Send className="w-4 h-4" />
          {submitting ? 'Sending...' : 'Send it into the realm'}
        </button>
      </div>
    </form>
  );
};

const ContactSidebar = () => (
  <div className="lg:col-span-2 flex flex-col justify-center">
    <div className="border-l border-[#3d2a5c]/20 pl-8">
      <h3 className="text-lg font-medium text-[#F0EDE8] mb-6" data-testid="what-happens-next-heading">What happens next</h3>
      <ul className="space-y-5">
        {NEXT_STEPS.map((step) => (
          <li key={step} className="flex items-start gap-3">
            <Check className="w-4 h-4 text-[#14B8A6] flex-shrink-0 mt-1.5" strokeWidth={1.5} />
            <span className="text-[#E8E4DF]">{step}</span>
          </li>
        ))}
      </ul>
    </div>
  </div>
);

const Contact = () => (
  <section id="contact" className="py-24 lg:py-36 bg-[#0A0A0B]" data-testid="contact-section">
    <div className="container mx-auto px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-16">
          <p className="label-sm text-[#14B8A6] mb-4" data-font="label">Contact</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-medium text-[#F0EDE8] mb-5" data-testid="contact-heading">
            Let's bring your world to life
          </h2>
          <p className="text-[#E8E4DF] max-w-2xl mx-auto">
            If your story is full of atmosphere, danger, longing, beauty, obsession, or characters who refuse to stay imaginary, send it over. I'd love to hear what you're building.
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16">
          <div className="lg:col-span-3">
            <ContactForm />
            <div className="mt-6 text-center">
              <a href="mailto:info@whiskywoven.com" className="text-[#E8E4DF]/60 hover:text-[#14B8A6] transition-colors inline-flex items-center gap-2" data-testid="contact-email-link" data-font="label">
                <Mail className="w-4 h-4" />
                <span className="label-sm" style={{ textTransform: 'none', letterSpacing: '0.02em' }}>info@whiskywoven.com</span>
              </a>
            </div>
          </div>
          <ContactSidebar />
        </div>
      </div>
    </div>
  </section>
);

export default Contact;
